import React, { createContext, useContext, useState, ReactNode } from 'react';
import { startOfYear, endOfYear } from 'date-fns';

export type DateRangePreset = 'YTD' | '2025' | 'custom';

export interface DateRange {
  from: Date;
  to: Date;
  preset: DateRangePreset;
}

interface DateRangeContextType {
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  getPresetRange: (preset: DateRangePreset) => DateRange;
  selectedYear: number;
  setSelectedYear: (year: number) => void;
}

const DateRangeContext = createContext<DateRangeContextType | undefined>(undefined);

export const useDateRange = () => {
  const context = useContext(DateRangeContext);
  if (!context) {
    throw new Error('useDateRange must be used within a DateRangeProvider');
  }
  return context;
};

interface DateRangeProviderProps {
  children: ReactNode;
}

export const DateRangeProvider: React.FC<DateRangeProviderProps> = ({ children }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const getPresetRange = (preset: DateRangePreset): DateRange => {
    const today = new Date();
    
    switch (preset) {
      case 'YTD':
        return {
          from: startOfYear(today), // Calendar year start (January 1st of current year)
          to: today,
          preset
        };
      case '2025':
        return {
          from: new Date(2025, 0, 1), // January 1st, 2025
          to: endOfYear(new Date(2025, 0, 1)), // December 31st, 2025
          preset
        };
      case 'custom':
        return {
          from: startOfYear(today),
          to: today,
          preset
        };
      default:
        return {
          from: startOfYear(today),
          to: today,
          preset: 'YTD'
        };
    }
  };

  const [dateRange, setDateRange] = useState<DateRange>(() => getPresetRange('YTD'));

  return (
    <DateRangeContext.Provider
      value={{
        dateRange,
        setDateRange,
        getPresetRange,
        selectedYear,
        setSelectedYear,
      }}
    >
      {children}
    </DateRangeContext.Provider>
  );
};